import React from "react";
import { Field, reduxForm, SubmissionError } from "redux-form";
import { checkIfEmpty, validate } from "../validation/supportCardLevelForm";
import {
  CARD_ISSUES_WITHOUT_CLAIM,
  CARD_ISSUES_WITH_CLAIM,
  MISSING_INFO_OPTIONS,
  CARD_NUMBER_INVALID_OPTIONS,
  BALANCE_DISCREPANCY_OPTIONS,
  NOT_ACCEPTED_BY_MERCHANT_OPTIONS,
} from "../../../constants/customerSupport";

const renderField = (props) => {
  const {
    input,
    type,
    placeholder,
    className,
    claimField,
    orderAssistance,
    meta: { error, valid },
  } = props;
  const errorToShow = error ? error[0] : null;
  return (
    <div>
      <input
        {...input}
        className={className}
        type={type}
        placeholder={placeholder}
      ></input>
      {errorToShow ? (
        <div className="errorMsg mar-t-3">{errorToShow}</div>
      ) : null}
      {claimField && valid && input.value ? (
        <div className="claim-refund-amt mar-t-3">{`Upon approval, the refund for this claim will be $${orderAssistance.get(
          "refundAmount"
        )}.`}</div>
      ) : null}
    </div>
  );
};

const renderTextArea = ({
  input,
  type,
  placeholder,
  className,
  meta: { error },
}) => {
  const errorToShow = error ? error[0] : null;
  return (
    <div>
      <textarea
        {...input}
        className={className}
        rows="15"
        type={type}
        placeholder={placeholder}
      />
      {errorToShow ? <div className="errorMsg">{errorToShow}</div> : null}
    </div>
  );
};

const createDropdown = (defaultSelected, options) => {
  return ({ input, className, meta: { error }, field }) => {
    const errorToShow = error ? error[0] : null;
    return (
      <div>
        <select {...input} className={className}>
          <option value="" disabled="disabled">
            {defaultSelected}
          </option>
          {options.map((issue) => (
            <option data-id={issue.id} value={issue.display} key={issue.key}>
              {issue.display}
            </option>
          ))}
        </select>
        {errorToShow ? (
          <div className="errorMsg mar-t-3">{errorToShow}</div>
        ) : null}
      </div>
    );
  };
};

const SupportFormCardLevel = (props) => {
  const {
    handleSubmit,
    closeSupportCardLevelModal,
    orderAssistance,
    formData,
    setClaimType,
    sendSupportMessage,
    sendClaim,
    setClaimAmountToRefund,
    reset,
  } = props;

  const submit = (fields) => {
    const response = checkIfEmpty(fields);

    if (Object.keys(response).length) {
      throw new SubmissionError(response);
    }

    const formValues = formData.supportCardLevel.values;

    const supportBase = {
      name: orderAssistance.get("name"),
      email: orderAssistance.get("email"),
      orderId: orderAssistance.get("orderId"),
      orderType: orderAssistance.get("orderType"),
    };

    if (
      formValues.issue === "Other" ||
      formValues.issue === "Did not receive my card"
    ) {
      return sendSupportMessage({
        ...supportBase,
        subject: fields.subject ? fields.subject : formValues.issue,
        message: fields.message,
      });
    }

    return sendClaim({
      ...supportBase,
      subject: `${formValues.issue} | ${formValues.secondaryIssue}`,
      message: fields.message,
      merchantName: orderAssistance.get("merchantName"),
      cardId: orderAssistance.get("fulfilledId"),
      faceValue: Number(orderAssistance.get("faceValue")),
      cardType: orderAssistance.get("cardType"),
      claimValue: Number(fields.claimAmount),
      previousClaimValue: 0,
      claimType: Number(orderAssistance.get("claimType")),
    });
  };

  let cardIssue = null;
  let primaryDropdown;
  let secondaryDropdown;
  let claimAmount;
  let reachedClaimMax = false;

  if (
    orderAssistance.get("prevClaimAmount") >=
    Number(orderAssistance.get("faceValue"))
  ) {
    reachedClaimMax = true;
  }

  // NOTE: As per issue #2453 in Redux Form, must create single instance to prevent memory leaks (creating new function every time form is rendered)
  const createValidateFunction = (prevClaimAmount, faceValue) => (
    claimAmount
  ) => {
    return validate(claimAmount, prevClaimAmount, faceValue);
  };

  const validateClaim = createValidateFunction(
    orderAssistance.get("prevClaimAmount"),
    orderAssistance.get("faceValue")
  );
  //

  if (
    formData &&
    formData.supportCardLevel &&
    formData.supportCardLevel.values
  ) {
    const values = formData.supportCardLevel.values;
    cardIssue = values.issue ? values.issue : null;
    claimAmount = values.claimAmount ? values.claimAmount : null;
  }

  switch (cardIssue) {
    case "Card missing information":
      secondaryDropdown = createDropdown(
        "What's missing?",
        MISSING_INFO_OPTIONS
      );
      break;
    case "Card number invalid":
      secondaryDropdown = createDropdown(
        "Where did you attempt to use it?",
        CARD_NUMBER_INVALID_OPTIONS
      );
      break;
    case "Balance discrepancy":
      secondaryDropdown = createDropdown(
        "Where did you attempt to use it?",
        BALANCE_DISCREPANCY_OPTIONS
      );
      break;
    case "Not accepted by merchant":
      secondaryDropdown = createDropdown(
        "Where did you attempt to use it?",
        NOT_ACCEPTED_BY_MERCHANT_OPTIONS
      );
      break;
    default:
  }

  const handleChange = (e) => {
    const selectedOption = e.target.options[e.target.options.selectedIndex];
    setClaimType(selectedOption.getAttribute("data-id"));
    reset();
  };

  const calculateClaimAmountToRefund = (e) => {
    const cost = orderAssistance.get("cost");
    const faceValue = orderAssistance.get("faceValue");
    const claimPercentage = Number(e.target.value) / faceValue;
    const claimAmountToRefund = parseFloat(cost * claimPercentage).toFixed(2);

    if (/^\d+(,\d{3})*(\.\d*)?$/.test(claimAmountToRefund)) {
      setClaimAmountToRefund(claimAmountToRefund);
    }
  };

  if (!orderAssistance.get("fulfilledId") || reachedClaimMax) {
    primaryDropdown = createDropdown("Select Issue", CARD_ISSUES_WITHOUT_CLAIM);
  } else {
    primaryDropdown = createDropdown("Select Issue", CARD_ISSUES_WITH_CLAIM);
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="mar-b-20">
        <Field
          name="issue"
          component={primaryDropdown}
          onChange={handleChange}
          className="cc-input"
        />
        {secondaryDropdown ? (
          <Field
            name="secondaryIssue"
            component={secondaryDropdown}
            className="cc-input mar-t-10"
          />
        ) : null}
        {cardIssue === "Other" ? (
          <div>
            <Field
              name="subject"
              component={renderField}
              type="text"
              placeholder="Subject"
              className="cc-input mar-t-10"
            />
          </div>
        ) : null}
        {cardIssue === "Balance discrepancy" ||
        cardIssue === "Card missing information" ||
        cardIssue === "Card number invalid" ||
        cardIssue === "Not accepted by merchant" ? (
          <Field
            name="claimAmount"
            component={renderField}
            validate={[validateClaim]}
            onChange={calculateClaimAmountToRefund}
            type="text"
            placeholder="Please enter amount missing from card"
            className="cc-input mar-t-10"
            claimField
            orderAssistance={orderAssistance}
          />
        ) : null}
        <Field
          name="message"
          component={renderTextArea}
          type="text"
          placeholder="Please describe your issue in detail so we can assist you."
          className="cc-input modal-text-area mar-t-10"
        />
      </div>
      <div className="modal-input-mar-b-20">
        <div className="modal-input-flex-2-pad-r-10">
          <button type="submit" className="acct-pg-btn acct-pg-btn-block">
            Submit
          </button>
        </div>
        <div className="modal-input-pad-l-10">
          <div
            className="acct-pg-btn-inverse acct-pg-btn-block"
            onClick={closeSupportCardLevelModal}
          >
            Close
          </div>
        </div>
      </div>
    </form>
  );
};

export default reduxForm({
  form: "supportCardLevel",
  enableReinitialize: true,
})(SupportFormCardLevel);
